<template>
  <div>

    <div class="page-header">
      <div>
        <h5 class="page-title">Announcements</h5>
        <p class="page-sub">Company news and updates from your HR team</p>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="ann-list">
      <div v-for="n in 4" :key="n" class="sk-card">
        <div class="sk-line sk-line--title"></div>
        <div class="sk-line sk-line--body"></div>
        <div class="sk-line sk-line--body sk-line--short"></div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="!list.length" class="empty-box">
      <div class="empty-icon-wrap"><font-awesome-icon icon="bullhorn" /></div>
      <p class="empty-title">No announcements yet</p>
      <p class="empty-sub">Check back later for company news and updates.</p>
    </div>

    <!-- Announcement cards -->
    <div v-else class="ann-list">
      <div v-for="item in list" :key="item.id" class="ann-card">

        <!-- Header -->
        <div class="ann-card-head">
          <div class="ann-meta-wrap">
            <div class="ann-icon-wrap"><font-awesome-icon icon="bullhorn" /></div>
            <div>
              <p class="ann-title">{{ item.title }}</p>
              <p class="ann-date">{{ fmtDate(item.publish_at || item.created_at) }}</p>
            </div>
          </div>
          <span class="audience-chip" :class="audienceClass(item.audience)">
            {{ audienceLabel(item.audience) }}
          </span>
        </div>

        <!-- Body -->
        <div class="ann-body" :class="{ 'ann-body--collapsed': !item.expanded }" v-html="item.body"></div>
        <button v-if="stripHtml(item.body).length > 200" class="btn-expand" @click="item.expanded = !item.expanded">
          {{ item.expanded ? 'Show less' : 'Read more' }}
        </button>

        <!-- Interactions bar -->
        <div class="ann-actions">
          <button
            class="action-btn"
            :class="{ 'action-btn--liked': item.user_has_liked }"
            @click="toggleLike(item)"
          >
            <font-awesome-icon :icon="item.user_has_liked ? 'heart' : 'heart'" />
            <span>{{ item.likes_count || 0 }}</span>
          </button>

          <button class="action-btn" @click="toggleComments(item)">
            <font-awesome-icon icon="comment" />
            <span>{{ item.comments_count || 0 }}</span>
          </button>
        </div>

        <!-- Comments section -->
        <div v-if="item.showComments" class="comments-section">

          <!-- Loading comments -->
          <div v-if="item.commentsLoading" class="comments-loading">
            <font-awesome-icon icon="spinner" spin /> Loading comments...
          </div>

          <!-- Comment list -->
          <div v-else-if="item.comments && item.comments.length" class="comments-list">
            <div v-for="c in item.comments" :key="c.id" class="comment-item">
              <div class="comment-avatar">{{ initials(c.user_name) }}</div>
              <div class="comment-bubble">
                <div class="comment-name">{{ c.user_name }}</div>
                <div class="comment-text">{{ c.body }}</div>
                <div class="comment-footer">
                  <span class="comment-time">{{ timeAgo(c.created_at) }}</span>
                  <button v-if="c.is_mine" class="btn-del-comment" @click="deleteComment(item, c)" title="Delete">
                    <font-awesome-icon icon="trash" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <p v-else class="no-comments">No comments yet. Be the first to comment.</p>

          <!-- Add comment -->
          <div class="add-comment">
            <div class="comment-avatar comment-avatar--me">{{ myInitials }}</div>
            <div class="comment-input-wrap">
              <textarea
                v-model="item.newComment"
                class="comment-input"
                placeholder="Write a comment..."
                rows="1"
                @keydown.enter.exact.prevent="submitComment(item)"
              ></textarea>
              <button class="btn-send" :disabled="!item.newComment?.trim() || item.submitting" @click="submitComment(item)">
                <font-awesome-icon :icon="item.submitting ? 'spinner' : 'paper-plane'" :spin="item.submitting" />
              </button>
            </div>
          </div>

        </div>

      </div>

      <!-- Pagination -->
      <pagination
        :pagination="pagination"
        @prev="goPage(pagination.currentPage - 1)"
        @next="goPage(pagination.currentPage + 1)"
      />
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import Pagination from '../../../../components/Pagination.vue';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)     return 'Just now';
  if (diff < 3600)   return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400)  return Math.floor(diff / 3600) + 'h ago';
  if (diff < 604800) return Math.floor(diff / 86400) + 'd ago';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default {
  name: 'MyAnnouncements',

  components: { Pagination },

  data() {
    return {
      list:    [],
      loading: false,
      page:    1,
      pagination: {
        currentPage: 1,
        lastPage:    1,
        total:       0,
        from:        0,
        to:          0,
        prevPageUrl: null,
        nextPageUrl: null,
      },
    };
  },

  computed: {
    currentUser() {
      return this.$store?.state?.getLoggedInUser || {};
    },
    myInitials() {
      const u = this.currentUser;
      const f = (u.first_name || '').charAt(0).toUpperCase();
      const l = (u.last_name  || '').charAt(0).toUpperCase();
      return f + l || '?';
    },
  },

  mounted() {
    this.fetchList();
  },

  methods: {

    async fetchList() {
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/self-service/announcements', { params: { page: this.page, length: 10 } });
        const raw = data.data?.data || data.data || [];
        this.list = raw.map(a => ({
          ...a,
          expanded:       false,
          showComments:   false,
          commentsLoading: false,
          comments:       null,
          newComment:     '',
          submitting:     false,
        }));
        const p = data.data || {};
        this.pagination.currentPage = p.current_page ?? 1;
        this.pagination.lastPage    = p.last_page    ?? 1;
        this.pagination.total       = p.total        ?? 0;
        this.pagination.from        = p.from         ?? 0;
        this.pagination.to          = p.to           ?? 0;
        this.pagination.prevPageUrl = p.prev_page_url ?? null;
        this.pagination.nextPageUrl = p.next_page_url ?? null;
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load announcements');
      } finally {
        this.loading = false;
      }
    },

    goPage(p) {
      this.page = p;
      this.fetchList();
    },

    // ── Like ──────────────────────────────────────────────────────────────────
    async toggleLike(item) {
      try {
        const { data } = await axios.post(`/hr/announcements/${item.id}/like`);
        item.user_has_liked  = data.data.liked;
        item.likes_count     = data.data.like_count;
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Action failed');
      }
    },

    // ── Comments ──────────────────────────────────────────────────────────────
    async toggleComments(item) {
      item.showComments = !item.showComments;
      if (item.showComments && item.comments === null) {
        await this.loadComments(item);
      }
    },

    async loadComments(item) {
      item.commentsLoading = true;
      try {
        const { data } = await axios.get(`/hr/announcements/${item.id}/comments`);
        item.comments = data.data || [];
      } catch (e) {
        item.comments = [];
      } finally {
        item.commentsLoading = false;
      }
    },

    async submitComment(item) {
      const body = item.newComment?.trim();
      if (!body) return;
      item.submitting = true;
      try {
        const { data } = await axios.post(`/hr/announcements/${item.id}/comments`, { body });
        item.comments = [data.data, ...(item.comments || [])];
        item.comments_count = (item.comments_count || 0) + 1;
        item.newComment = '';
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to post comment');
      } finally {
        item.submitting = false;
      }
    },

    async deleteComment(item, comment) {
      if (!confirm('Delete this comment?')) return;
      try {
        await axios.delete(`/hr/announcements/${item.id}/comments/${comment.id}`);
        item.comments = item.comments.filter(c => c.id !== comment.id);
        item.comments_count = Math.max(0, (item.comments_count || 1) - 1);
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Delete failed');
      }
    },

    // ── Helpers ───────────────────────────────────────────────────────────────
    stripHtml(html) {
      return (html || '').replace(/<[^>]*>/g, '').trim();
    },
    fmtDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    timeAgo,
    initials(name) {
      if (!name) return '?';
      return name.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    },
    audienceLabel(a) {
      return { all: 'All Staff', department: 'Department', position: 'Position' }[a] || 'All Staff';
    },
    audienceClass(a) {
      return { all: 'chip--blue', department: 'chip--purple', position: 'chip--amber' }[a] || 'chip--blue';
    },
  },
};
</script>

<style scoped>
.page-header { margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

/* ── Empty ────────────────────────────────────────────────────── */
.empty-box       { text-align:center; padding:64px 24px; }
.empty-icon-wrap { width:64px; height:64px; border-radius:50%; background:rgba(99,102,241,.1); color:#6366f1; display:flex; align-items:center; justify-content:center; font-size:26px; margin:0 auto 16px; }
.empty-title     { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.empty-sub       { font-size:13px; color:#7a90b0; margin:0; }

/* ── Skeleton ─────────────────────────────────────────────────── */
.sk-card       { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:24px; margin-bottom:16px; }
.sk-line       { background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:6px; height:14px; margin-bottom:10px; }
.sk-line--title { height:18px; width:60%; }
.sk-line--body  { width:100%; }
.sk-line--short { width:75%; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* ── Card ─────────────────────────────────────────────────────── */
.ann-list { display:flex; flex-direction:column; gap:16px; }
.ann-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:0; overflow:hidden; transition:box-shadow .2s; }
.ann-card:hover { box-shadow:0 4px 20px rgba(0,0,0,.07); }

.ann-card-head   { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; padding:20px 20px 0; }
.ann-meta-wrap   { display:flex; align-items:flex-start; gap:12px; flex:1; min-width:0; }
.ann-icon-wrap   { width:38px; height:38px; border-radius:10px; background:rgba(99,102,241,.1); color:#6366f1; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
.ann-title       { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.ann-date        { font-size:12px; color:#7a90b0; margin:0; }

.ann-body          { font-size:14px; color:#3d5068; line-height:1.65; padding:14px 20px 0; margin:0; word-break:break-word; }
.ann-body--collapsed { display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }
.ann-body :deep(p)  { margin:0 0 8px; }
.ann-body :deep(p:last-child) { margin-bottom:0; }
.ann-body :deep(ul),.ann-body :deep(ol) { padding-left:20px; margin:0 0 8px; }
.ann-body :deep(li) { margin-bottom:3px; }
.ann-body :deep(strong) { font-weight:700; color:#1a2b45; }
.ann-body :deep(blockquote) { border-left:3px solid #004AAD; padding-left:12px; margin:8px 0; color:#5a7090; font-style:italic; }
.ann-body :deep(a) { color:#004AAD; }
.btn-expand        { background:none; border:none; cursor:pointer; color:#004AAD; font-size:12.5px; font-weight:600; padding:6px 20px 0; display:block; }

/* ── Audience chip ────────────────────────────────────────────── */
.audience-chip  { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; white-space:nowrap; }
.chip--blue     { background:#dbeafe; color:#1d4ed8; }
.chip--purple   { background:#ede9fe; color:#7c3aed; }
.chip--amber    { background:#fef9c3; color:#ca8a04; }

/* ── Action bar ───────────────────────────────────────────────── */
.ann-actions  { display:flex; align-items:center; gap:4px; padding:12px 20px; border-top:1px solid #f4f7fb; margin-top:14px; }
.action-btn   { display:inline-flex; align-items:center; gap:6px; background:none; border:none; cursor:pointer; color:#7a90b0; font-size:13px; font-weight:600; padding:6px 12px; border-radius:8px; transition:all .15s; }
.action-btn:hover { background:#f4f7fb; color:#1a2b45; }
.action-btn--liked { color:#e03535; }
.action-btn--liked:hover { color:#c02828; background:#fff0f0; }

/* ── Comments section ─────────────────────────────────────────── */
.comments-section  { border-top:1px solid #f0f4f8; padding:16px 20px; display:flex; flex-direction:column; gap:12px; background:#fafbfd; }
.comments-loading  { font-size:13px; color:#7a90b0; display:flex; align-items:center; gap:8px; }
.comments-list     { display:flex; flex-direction:column; gap:10px; }
.no-comments       { font-size:13px; color:#aab8cc; margin:0; text-align:center; }

/* ── Comment item ─────────────────────────────────────────────── */
.comment-item   { display:flex; align-items:flex-start; gap:10px; }
.comment-avatar { width:32px; height:32px; border-radius:50%; background:#004AAD; color:#fff; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex-shrink:0; }
.comment-bubble { background:#fff; border:1px solid #e4eaf2; border-radius:0 10px 10px 10px; padding:10px 12px; flex:1; min-width:0; }
.comment-name   { font-size:12px; font-weight:700; color:#1a2b45; margin-bottom:3px; }
.comment-text   { font-size:13px; color:#3d5068; line-height:1.5; word-break:break-word; }
.comment-footer { display:flex; align-items:center; justify-content:space-between; margin-top:6px; }
.comment-time   { font-size:11px; color:#aab8cc; }
.btn-del-comment { background:none; border:none; cursor:pointer; color:#aab8cc; font-size:11px; padding:0; }
.btn-del-comment:hover { color:#e03535; }

/* ── Add comment ──────────────────────────────────────────────── */
.add-comment       { display:flex; align-items:flex-start; gap:10px; }
.comment-avatar--me { background:#16a34a; }
.comment-input-wrap { display:flex; align-items:flex-end; gap:6px; flex:1; }
.comment-input  { flex:1; padding:9px 12px; border:1px solid #dde5f0; border-radius:10px; font-size:13px; color:#1a2b45; outline:none; resize:none; font-family:inherit; line-height:1.4; }
.comment-input:focus { border-color:#004AAD; }
.btn-send { width:34px; height:34px; border-radius:8px; background:#004AAD; color:#fff; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; transition:background .15s; }
.btn-send:hover:not(:disabled) { background:#003b8e; }
.btn-send:disabled { opacity:.4; cursor:not-allowed; }

</style>
